import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { ArrowRight } from "lucide-react";

const HeroSection = ({ onPreorderClick }) => {
  const { t, language } = useLanguage();

  return (
    <section className="relative min-h-screen gradient-warm pattern-dots pt-20 overflow-hidden" data-testid="hero-section">
      {/* Animated glow orbs */}
      <div className="glow-orb w-[500px] h-[500px] bg-orange-300 top-0 -right-40"></div>
      <div className="glow-orb w-[400px] h-[400px] bg-yellow-300 bottom-20 -left-20"></div>
      <div className="glow-orb w-[300px] h-[300px] bg-pink-200 top-1/3 left-1/4"></div>
      
      <div className="calli-container relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20 py-12 lg:py-20">
          
          {/* Content */}
          <div className="flex-1 text-center lg:text-left space-y-8 animate-slide-up">
            <div className="inline-flex items-center gap-2">
              <span className="calli-badge">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                {t("hero_badge")}
              </span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black leading-[0.95] tracking-tight text-gray-900">
              {t("hero_title")}{" "}
              <span className="gradient-text">{t("hero_title_highlight")}</span>{" "}
              <br className="hidden sm:block" />
              {t("hero_title_end")}
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
              {t("hero_subtitle")}
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <button 
                onClick={onPreorderClick}
                className="calli-btn-primary text-lg px-10 w-full sm:w-auto"
                data-testid="hero-cta"
              >
                {t("hero_cta")}
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              <p className="text-sm text-gray-500">{t("hero_cta_sub")}</p>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 pt-4">
              {[
                { icon: "🔒", text: "100% Secure" },
                { icon: "💚", text: "Eco-friendly" },
                { icon: "🇫🇷", text: "Made in France" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                  <span>{item.icon}</span>
                  <span className="font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Phone Visual - Real Product Photo */}
          <div className="flex-1 relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Subtle glow behind image */}
              <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-3xl blur-3xl opacity-30 scale-110"></div>
              
              {/* Real product image */}
              <div className="relative animate-float">
                <img 
                  src="https://customer-assets.emergentagent.com/job_calli-portal/artifacts/j6uokuwd_Photoroom_20260304_214407.PNG"
                  alt="Calli - Le téléphone sans écran pour enfants"
                  className="w-64 sm:w-80 lg:w-96 h-auto drop-shadow-2xl"
                  data-testid="hero-product-image"
                />
                
                {/* Badges touching the phone */}
                <div className="absolute top-4 right-0 sm:top-6 sm:right-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-xl animate-bounce-in">
                  15 {language === 'fr' ? 'jours' : 'days'} 🔋
                </div>
                <div className="absolute bottom-16 left-0 sm:bottom-20 sm:left-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full shadow-xl animate-bounce-in" style={{animationDelay: '0.2s'}}>
                  0 {language === 'fr' ? 'écran' : 'screen'} ✨
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Video Section - YouTube Embed */}
        <div className="py-12 lg:py-20">
          <div className="relative max-w-4xl mx-auto">
            {/* Glowing border */}
            <div className="absolute -inset-1 bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-400 rounded-3xl blur opacity-40"></div>
            
            <div className="relative bg-black rounded-3xl overflow-hidden shadow-2xl">
              <div className="aspect-video">
                <iframe
                  src={language === 'fr' 
                    ? "https://www.youtube-nocookie.com/embed/7sRYZE0R8eI?rel=0&modestbranding=1&playsinline=1&vq=hd1080&hd=1" 
                    : "https://www.youtube-nocookie.com/embed/r6vmR4D51jY?rel=0&modestbranding=1&playsinline=1&vq=hd1080&hd=1"
                  }
                  title={language === 'fr' ? 'Vidéo de présentation Calli' : 'Calli presentation video'}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                  loading="eager"
                  data-testid="hero-video"
                ></iframe>
              </div>
            </div>
            
            <p className="text-center text-sm font-medium text-gray-500 mt-4">
              ▶️ {language === 'fr' ? 'Découvrez Calli en action' : 'See Calli in action'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
